import { IDocumentField } from '@models/Forms/IForms';
import type { IFieldOverrides } from '@/context/DebugPanelContext';
import { IFormulaEvaluator } from './formulaEvaluator';
import { evaluateFieldErrors, IDebugValidationFlags, isDateValueType, isRequiredEmpty } from './evaluateFieldErrors';
import {
    getDocumentRequiredMessage,
    type IRequiredMessageField,
    type RequiredDocumentControl,
} from './requiredMessage';

/**
 * Layer 2 — оркестрация валидации одного поля.
 *
 * Вычисляет динамические значения (видимость/required/границы дат) через
 * инъектированный {@link IFormulaEvaluator} и делегирует решение чистому
 * Layer 1 ({@link evaluateFieldErrors}). Здесь нет прямой работы с
 * `FormulaManager` — это держит слой тестируемым фейковым вычислителем.
 */

/** Проверка, приходится ли дата на выходной по рабочему календарю. */
export type HolidayChecker = (date: Date) => boolean;

/** Поставщик {@link HolidayChecker} (заменяет хук useWorkCalendar в resolver-слое). */
export type HolidayCheckerProvider = () => Promise<HolidayChecker | undefined> | HolidayChecker | undefined;

export interface IFieldEvalOptions {
    debug?: IDebugValidationFlags;
    /** Поставщик проверки выходного дня по рабочему календарю (заменяет хук useWorkCalendar). */
    getHolidayChecker?: HolidayCheckerProvider;
    /**
     * Дебаг-оверрайды правил поля («жучок»). Семантика = Field.tsx: visibility/required
     * подменяют вычисление формулы, pattern — сырой атрибут, min/max — вычисленные границы дат
     * (гейты применения остаются на сырых field.min/max, как в checkDate).
     */
    override?: IFieldOverrides;
}

/** Полная валидация IDocumentField (как Field.tsx). Возвращает текст ошибки или undefined. */
export async function evaluateDocumentField(
    field: IDocumentField,
    value: unknown,
    evaluator: IFormulaEvaluator,
    opts: IFieldEvalOptions = {},
): Promise<string | undefined> {
    const override = opts.override;
    // visibilityRules: fallback true (как useFormulaState({fallback:true})).
    const visible = override?.visibility ?? (await evaluator.evalBool(field.visibilityRules, true));
    if (!visible) {
        return undefined;
    }

    const required = override?.required ?? (await evaluator.evalBool(field.required, false));
    const configuredMaxLength = override?.maxLength ?? field.maxLength;
    const maxLengthEnabled = await evaluator.evalBool(field.maxLengthRules, true);
    const maxLength = maxLengthEnabled && configuredMaxLength != null ? configuredMaxLength : undefined;
    const maxLengthDisabledByRule = !!field.maxLengthRules && !maxLengthEnabled;

    let minVal: string | number | null | undefined;
    let maxVal: string | number | null | undefined;
    let workCalendarActive = false;
    let isHoliday: HolidayChecker | undefined;

    if (isDateValueType(field.valueType)) {
        if (field.min) minVal = await evaluator.evalReplace(field.min);
        if (field.max) maxVal = await evaluator.evalReplace(field.max);
        if (override?.min !== undefined) minVal = override.min;
        if (override?.max !== undefined) maxVal = override.max;
        if (field.workCalendarRules) {
            workCalendarActive = await evaluator.evalBool(field.workCalendarRules, false);
            if (workCalendarActive && opts.getHolidayChecker) {
                isHoliday = (await opts.getHolidayChecker()) ?? undefined;
            }
        }
    }

    return evaluateFieldErrors({
        valueType: field.valueType,
        requiredMessage: field.requiredMessage,
        inputType: field.inputType,
        trueText: field.trueText,
        falseText: field.falseText,
        value,
        visible: true,
        required,
        pattern: override?.pattern ?? field.pattern,
        min: field.min,
        max: field.max,
        maxLength,
        maxLengthDisabledByRule,
        minVal,
        maxVal,
        workCalendarActive,
        isHoliday,
        debug: opts.debug,
    });
}

/**
 * Required-only валидация для контролов dict/abook/autoComplete/calc.
 *
 * В Field-слое они отдают в RHF только правило `required` (см. Dict.tsx/Abook.tsx),
 * поэтому здесь проверяется только пустое обязательное видимое поле.
 */
export async function evaluateRequiredControl(
    control: { required: string; visibilityRules: string } & IRequiredMessageField,
    value: unknown,
    evaluator: IFormulaEvaluator,
    opts: { debug?: IDebugValidationFlags; override?: IFieldOverrides; controlType?: RequiredDocumentControl } = {},
): Promise<string | undefined> {
    const skipReq = !!opts.debug?.skipAllValidation || !!opts.debug?.skipRequiredValidation;
    if (skipReq) return undefined;

    const visible = opts.override?.visibility ?? (await evaluator.evalBool(control.visibilityRules, true));
    if (!visible) return undefined;

    const required = opts.override?.required ?? (await evaluator.evalBool(control.required, false));
    if (required && isRequiredEmpty(value)) {
        return getDocumentRequiredMessage(control, opts.controlType ?? 'dict');
    }
    return undefined;
}
